import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppBarTitleService, AppBarActionsService, AppBarAction, FeatureFlagService, states, AppPageService, SimpleDialogService, CommonActionKey } from 'common';
import { UserPermissionService } from '../../../user/user-permission/user-permission.service';
import { Tenant } from '../tenant.model';
import { readWrite } from '../../../user/user-permission/user-permission.data';
import { FeatureFlagListComponent } from '../../feature-flag/feature-flag-list/feature-flag-list.component';

@UntilDestroy()
@Component({
    selector: 'ifb-tenant-details',
    templateUrl: './tenant-details.component.html',
    styleUrls: ['./tenant-details.component.scss'],
    standalone: false
})
export class TenantDetailsComponent implements OnInit {
  form: UntypedFormGroup;
  data: TenantDetailsComponentData;
  canReadWrite: boolean;

  readonly states = states;

  @ViewChild(FeatureFlagListComponent) featureFlagListComponent;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly service: FeatureFlagService,
    private readonly userPermissionService: UserPermissionService,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly simpleDialogService: SimpleDialogService,
  ) {
    this.appBarActionsService.actions = [
      { id: 'cancel', label: 'Cancel', buttonType: 'button' },
      { id: 'save', label: 'Save', disabled: true, buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' }
    ];

    this.initForm();
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this))
      .subscribe(this.initData.bind(this));

    this.appBarActionsService.invoking.pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));

    this.userPermissionService.granted([readWrite('admin-tenants'), readWrite('admin-feature-flags')])
      .subscribe(res => {
        this.canReadWrite = res;
        this.actionUpdate();
      });
  }

  private initData(data: TenantDetailsComponentData): void {
    this.data = data;
    this.appBarTitleService.title = this.data.tenant ? `Tenant ${this.data.tenant.name}` : 'Tenant info';

    if (this.data.tenant) {
      this.form.reset({
        name: this.data.tenant.name || '',
        dba: this.data.tenant.dba || '',
        careOf: this.data.tenant.careOf || '',
        phone: this.data.tenant.phone || '',
        email: this.data.tenant.infoEmail || '',
        url: this.data.tenant.url || '',
        street: this.data.tenant.headquartersAddress ? this.data.tenant.headquartersAddress.line1 : '',
        city: this.data.tenant.headquartersAddress ? this.data.tenant.headquartersAddress.city : '',
        state: this.data.tenant.headquartersAddress ? this.data.tenant.headquartersAddress.state : '',
        zipCode: this.data.tenant.headquartersAddress ? this.data.tenant.headquartersAddress.zip : '',
        unitNumber: this.data.tenant.headquartersAddress ? this.data.tenant.headquartersAddress.line2 : ''
      });
    } else {
      this.form.reset();
    }

    this.form.markAsPristine();
  }

  private actionUpdate(): void {
    this.appBarActionsService.enable('save', this.canReadWrite);
  }

  private actionDispatch(action: AppBarAction): void  {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  cancel(): void  {
    this.appPageService.back();
  }

  save(): void  {
    this.simpleDialogService.openSimpleConfirmDialog(`Save changes to ${this.data.tenant.name}?`, "Confirm")
    .subscribe((result: CommonActionKey) => {
      if (result === CommonActionKey.Confirm) {
        this.service.updateByTenant(this.data.tenant.id, this.featureFlagListComponent.updatedFeatures)
        .pipe(untilDestroyed(this))
        .subscribe({ next: () => this.router.navigate(['/admin/tenants']) })
      }
    });
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      name: undefined,
      dba: undefined,
      careOf: undefined,
      phone: [undefined, Validators.compose([Validators.minLength(10), Validators.maxLength(10)])],
      email: undefined,
      url: undefined,
      street: [undefined, Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(255)])],
      city: [undefined, Validators.compose([Validators.required, Validators.pattern(/^([a-zA-Z\u0080-\u024F]+(?:. |-| |'))*[a-zA-Z\u0080-\u024F]*$/), Validators.minLength(2), Validators.maxLength(40)])],
      state: [undefined, Validators.required],
      zipCode: [undefined, Validators.compose([Validators.required, Validators.pattern(/^\d{5}(-?\d{4})?$/), Validators.maxLength(20)])],
      unitNumber: [undefined, Validators.maxLength(16)]
    });
  }
}

export interface TenantDetailsComponentData {
  tenant?: Tenant;
}
